import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { Link, useNavigate } from 'react-router-dom'
import useMediaQuery from '@mui/material/useMediaQuery'
import Container from '@mui/material/Container'
import AppBar from '@mui/material/AppBar'
import IconButton from '@mui/material/IconButton'
import Drawer from '@mui/material/Drawer'
import Typography from '@mui/material/Typography'
import { keyframes } from '@emotion/react'
import { useEffect, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

const menuItems = [
  { key: 'races', to: '/races', aria: 'racesAria' },
  { key: 'events', to: '/events', aria: 'eventsAria' },
  { key: 'social', to: '/social/groups', aria: 'socialAria' },
  { key: 'about', to: '/about', aria: 'aboutAria' }
]

export default function Header () {
  const { t, i18n } = useTranslation('common')
  const isRtl = (i18n.resolvedLanguage || i18n.language) === 'ar'
  const isSmall = useMediaQuery(theme => theme.breakpoints.down(576))
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const [scrollY, setScrollY] = useState(0)
  const [mobileOpen, setMobileOpen] = useState(false)

  const handleScroll = useCallback(() => {
    setScrollY(window.scrollY)
  }, [])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  const isScrolled = scrollY > 50

  const closeMenu = () => setMobileOpen(false)

  return (
    <AppBar
      variant='sticky'
      elevation={0}
      color='transparent'
      role='banner'
      aria-label={t('navAria')}
      sx={{
        backgroundColor: isScrolled ? 'rgba(0, 0, 0, 0.5)' : 'transparent',
        transition: 'background-color 0.3s ease-in-out',
        ...(isScrolled ? styles.appBar : { backdropFilter: '0px 0px' })
      }}
    >
      <Container>
        <Grid container spacing={2} sx={{ mt: 2, mb: 2, alignItems: 'center' }}>
          <Box
            component='img'
            src='/peaks-logo-white.png'
            sx={{ cursor: 'pointer', width: { xs: 72, md: 160 }, height: 'auto' }}
            onClick={() => { closeMenu(); navigate('/') }}
            alt={t('logoAlt')}
            aria-label={t('goHome')}
          />
          {isMobile
            ? (
              <IconButton
                aria-label={t('menuToggle', { defaultValue: 'Menu' })}
                aria-expanded={mobileOpen}
                aria-controls='mobile-menu'
                onClick={() => setMobileOpen(prev => !prev)}
                sx={styles.burgerButton}
              >
                <Box sx={styles.burgerBox}>
                  <Box component='span' sx={styles.burgerLine(mobileOpen, 0)} />
                  <Box component='span' sx={styles.burgerLine(mobileOpen, 1)} />
                  <Box component='span' sx={styles.burgerLine(mobileOpen, 2)} />
                </Box>
              </IconButton>
            )
            : (
              <Box sx={{ ml: { xs: 1, sm: 3 } }}>
                {menuItems.map(item => (
                  <Button
                    key={item.key}
                    component={Link}
                    to={item.to}
                    variant='text'
                    color='secondary'
                    size={isSmall ? 'small' : 'large'}
                    sx={styles.menuButtons}
                    aria-label={t(item.aria)}
                  >
                    {t(item.key)}
                  </Button>
                ))}
              </Box>
            )}
        </Grid>
      </Container>

      <Drawer
        id='mobile-menu'
        anchor={isRtl ? 'left' : 'right'}
        open={mobileOpen}
        onClose={closeMenu}
        slotProps={{
          paper: { sx: { ...styles.drawerPaper, ...(isRtl ? styles.drawerPaperRtl : {}) } },
          backdrop: { sx: styles.drawerBackdrop },
          transition: { direction: isRtl ? 'right' : 'left' }
        }}
        ModalProps={{ keepMounted: true }}
      >
        <Box sx={styles.drawerInner} role='navigation' aria-label={t('navAria')}>
          <Box sx={styles.drawerHeader}>
            <Box
              component='img'
              src='/peaks-logo-white.png'
              sx={{ cursor: 'pointer', width: 72, height: 'auto' }}
              onClick={() => { closeMenu(); navigate('/') }}
              alt={t('logoAlt')}
            />
            <IconButton
              aria-label={t('menuClose', { defaultValue: 'Close menu' })}
              onClick={closeMenu}
              sx={styles.closeButton}
            >
              <Box sx={styles.burgerBox}>
                <Box component='span' sx={styles.burgerLine(true, 0)} />
                <Box component='span' sx={styles.burgerLine(true, 1)} />
                <Box component='span' sx={styles.burgerLine(true, 2)} />
              </Box>
            </IconButton>
          </Box>

          <Box component='ul' sx={styles.menuList}>
            {menuItems.map((item, index) => (
              <Box
                key={item.key}
                component='li'
                sx={{ ...styles.menuItem, animation: `${styles.menuEntrance} 0.5s ease ${index * 0.08}s backwards` }}
              >
                <Button
                  component={Link}
                  to={item.to}
                  onClick={closeMenu}
                  sx={styles.menuLink}
                  aria-label={t(item.aria)}
                >
                  <Typography variant='h5' component='span' sx={styles.menuLinkText}>
                    {t(item.key)}
                  </Typography>
                  <Typography component='span' sx={styles.menuLinkArrow} aria-hidden='true'>
                    →
                  </Typography>
                </Button>
              </Box>
            ))}
          </Box>

          <Typography sx={styles.drawerFooter}>
            {t('footerTagline', { defaultValue: 'Push your limits, embrace the peaks.' })}
          </Typography>
        </Box>
      </Drawer>
    </AppBar>
  )
}

const menuEntrance = keyframes`
  from { opacity: 0; transform: translateY(24px) }
  to { opacity: 1; transform: translateY(0) }
`

const styles = {
  appBar: {
    backdropFilter: 'blur(4px)'
  },
  menuButtons: {
    fontWeight: 600,
    fontSize: '0.7rem',
    color: 'white !important',
    textShadow: '1px 1px 1px rgba(0,0,0,0.5)',
    mt: -1,
    mr: { xs: 0.3, sm: 2 }
  },
  burgerButton: {
    marginInlineStart: 'auto',
    p: 1.5,
    borderRadius: 2,
    color: 'white',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.1)'
    },
    '&:focus-visible': {
      outline: '2px solid rgba(255,255,255,0.7)',
      outlineOffset: 2
    }
  },
  burgerBox: {
    position: 'relative',
    width: 26,
    height: 20
  },
  burgerLine: (open, index) => ({
    position: 'absolute',
    left: 0,
    display: 'block',
    width: 26,
    height: 2,
    borderRadius: 2,
    backgroundColor: 'white',
    transformOrigin: 'center',
    transition: 'transform 0.35s ease, opacity 0.25s ease, top 0.35s ease',
    top: index === 0 ? 2 : index === 1 ? 9 : 16,
    ...(open
      ? index === 0
        ? { top: 9, transform: 'translateY(-50%) rotate(45deg)' }
        : index === 1
          ? { opacity: 0, transform: 'scaleX(0)' }
          : { top: 9, transform: 'translateY(-50%) rotate(-45deg)' }
      : {})
  }),
  drawerBackdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
    backdropFilter: 'blur(2px)'
  },
  drawerPaper: {
    width: '100%',
    maxWidth: 420,
    backgroundColor: 'rgba(12, 16, 28, 0.92)',
    backgroundImage: 'linear-gradient(160deg, rgba(60, 80, 140, 0.25) 0%, rgba(12, 16, 28, 0) 40%)',
    backdropFilter: 'blur(20px)',
    boxShadow: '-20px 0 60px rgba(0, 0, 0, 0.5)',
    borderLeft: '1px solid rgba(255,255,255,0.08)',
    color: 'white'
  },
  drawerPaperRtl: {
    boxShadow: '20px 0 60px rgba(0, 0, 0, 0.5)',
    borderLeft: 'none',
    borderRight: '1px solid rgba(255,255,255,0.08)'
  },
  drawerInner: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    pt: 2,
    px: 3,
    pb: 4
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    mb: 6
  },
  closeButton: {
    color: 'white',
    p: 1,
    borderRadius: 2,
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.1)'
    }
  },
  menuList: {
    listStyle: 'none',
    m: 0,
    p: 0,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  },
  menuItem: {
    m: 0,
    mb: 1.5,
    p: 0
  },
  menuLink: {
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
    p: '10px 12px',
    borderRadius: 3,
    color: 'white',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.08)',
      pl: '20px'
    },
    transition: 'background-color 0.25s ease, padding-left 0.25s ease'
  },
  menuLinkText: {
    fontWeight: 700,
    letterSpacing: 1
  },
  menuLinkArrow: {
    fontSize: '1.6rem',
    opacity: 0.5,
    transition: 'opacity 0.25s ease, transform 0.25s ease',
    '.MuiButtonBase-root:hover &': {
      opacity: 1,
      transform: 'translateX(4px)'
    }
  },
  drawerFooter: {
    textAlign: 'center',
    fontSize: '0.75rem',
    opacity: 0.6,
    letterSpacing: 0.5
  },
  menuEntrance
}
